import { ForwardedRef, useCallback, useEffect, useMemo, useState } from 'react'
import {
  HotelResultsFormState,
  HotelResultsFormStateSchema,
} from '@reward-platform/ancillaries-schemas/hotel'
import { Destination } from '@reward-platform/ancillaries-schemas/location'
import { AncillariesLocation } from '@reward-platform/ancillaries-locations'
import { add, addDays, addYears, min } from 'date-fns'
import { useRouter } from 'next/router'
import { useForm, UseFormReturn, useWatch } from 'react-hook-form'
import { useDebounceValue } from 'usehooks-ts'
import { useQueryClient } from '@tanstack/react-query'
import { z, ZodIssueCode } from 'zod'
import { zodFormResolver } from '~/utils/zodFormResolver'
import { usePlatformConfig, usePlatformRoutes } from '~/context/platform'
import { adjustDateObjectTimeToUserTime, preserveDateWhenConvertedToUTC } from '~/utils/datetime'
import { useFormValidationMessages } from '~/hooks/useMessages/'
import { useTranslations } from 'next-intl'
import { isClient } from '~/utils/envChecks'
import {
  createAncillariesLocationsQueryKey,
  useAncillariesLocationsSearch,
} from '~/hooks/useAncillariesLocations'
import useFilterRouter from '~/context/filters/useFilterRouter'
import {
  sanitizeBrowserIdentifier,
  sanitizeBrowserText,
} from '~/utils/security/sanitizeBrowserInput'
import QueryString from 'qs'
import { parseHotelUrlDestination } from '~/utils/locationComparison'
import { useLocationChanged } from '~/hooks/useLocationChanged'
import { addSearchToLocalStorage } from '../RecentSearches/RecentSearches'
import { DEFAULT_DESTINATION } from './parseHotelSearchUrl'
import { buildHotelResultsQuery } from './useHotelSearchParams'
import { setHotelDestinationHandoff } from './hotelDestinationHandoff'

export type UseDestinationStateReturn = {
  isLoading: boolean
  locations: AncillariesLocation[]
  searchString: string
  setSearchString: (value: string) => void
  noResultsFound: boolean
  onInputFocus: (e: React.FocusEvent<HTMLInputElement, Element>, openMenu: () => void) => void
  handleSuggestionClick: (key: string) => void
} & Pick<Destination, 'fullName'>

export type UseDateRangeStateReturn = {
  startDate: Date
  endDate: Date | null
  minDate: Date
  maxDate: Date
  maxEndDate: Date
  handleDateChange: (dates: [Date | null, Date | null]) => void
}

const shouldClearSelectedDestination = ({
  nextSearchString,
  selectedDestinationFullName,
}: {
  nextSearchString: string
  selectedDestinationFullName: string
}) => nextSearchString !== selectedDestinationFullName

const shouldSyncResolvedLocation = ({
  resolvedLocation,
  selectedDestinationFullName,
}: {
  resolvedLocation: HotelResultsFormState['destination']
  selectedDestinationFullName: string
}) => !selectedDestinationFullName || selectedDestinationFullName !== resolvedLocation.fullName

/**
 * Set up and manage state for the Destination autocomplete field
 */
export function useDestinationState(
  form: UseFormReturn<HotelResultsFormState>,
  resolvedLocation?: HotelResultsFormState['destination']
): UseDestinationStateReturn {
  const destinationFormFieldName: keyof HotelResultsFormState = 'destination'
  const [searchString, setSearchString] = useState<string>(resolvedLocation?.fullName ?? '')
  const destinationFormFieldValue = useWatch({
    control: form.control,
    name: destinationFormFieldName,
  })
  const [debouncedSearchString] = useDebounceValue(searchString ?? '', 500)

  // When re-focusing the input field, pause re-fetching until user edits the query and instead show previous data.
  const isSearchStringLocationAlreadySelected = searchString === destinationFormFieldValue.fullName
  const canSearch =
    debouncedSearchString !== '' &&
    debouncedSearchString === searchString &&
    !isSearchStringLocationAlreadySelected

  const isPendingDebounce =
    searchString !== debouncedSearchString &&
    searchString !== '' &&
    !isSearchStringLocationAlreadySelected

  const { data: locations, isFetching: isFetchingLocations } = useAncillariesLocationsSearch(
    'HOTEL',
    { searchString: debouncedSearchString },
    { isEnabled: canSearch }
  )

  const isLoading = isPendingDebounce || isFetchingLocations

  const noResultsFound = locations?.length === 0 && !isLoading && canSearch

  const onInputFocus = useCallback(
    (e: { target: { select: () => void } }, openMenu: () => void) => {
      e.target.select()
      openMenu()
    },
    []
  )

  const handleLocationSelect = useCallback(
    (location: HotelResultsFormState['destination']) => {
      setSearchString(location.fullName)
      form.clearErrors(destinationFormFieldName)
      form.setValue(destinationFormFieldName, location, { shouldDirty: true })
    },
    [form, destinationFormFieldName]
  )

  const updateSearchString = useCallback(
    (value: string) => {
      setSearchString(value)

      if (
        shouldClearSelectedDestination({
          nextSearchString: value,
          selectedDestinationFullName: form.getValues(destinationFormFieldName).fullName,
        })
      ) {
        form.setValue(destinationFormFieldName, DEFAULT_DESTINATION, { shouldDirty: true })
      }
    },
    [form, destinationFormFieldName]
  )

  const handleSuggestionClick = useCallback(
    (key: string) => {
      const location = locations?.find(({ id }) => id === key)
      if (location) {
        handleLocationSelect(location)
      }
    },
    [locations, handleLocationSelect]
  )

  useEffect(() => {
    const { fullName } = form.getValues(destinationFormFieldName)
    if (
      !!resolvedLocation &&
      shouldSyncResolvedLocation({
        resolvedLocation,
        selectedDestinationFullName: fullName,
      })
    ) {
      handleLocationSelect(resolvedLocation)
    }
  }, [form, handleLocationSelect, resolvedLocation])

  return {
    isLoading,
    locations: isLoading || !canSearch || !locations ? [] : locations,
    ...destinationFormFieldValue,
    searchString,
    setSearchString: updateSearchString,
    noResultsFound,
    onInputFocus,
    handleSuggestionClick,
  }
}

/**
 * Set up and manage the state for the date range fields
 */
export function useDateRangeState(
  form: UseFormReturn<HotelResultsFormState>,
  [defaultStartDate, defaultEndDate]: [Date, Date]
): UseDateRangeStateReturn {
  const maxStartDate = useMemo(() => new Date(addYears(Date.now(), 1)), [])
  const maxPossibleEndDate = useMemo(() => new Date(add(Date.now(), { years: 1, days: 28 })), [])
  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate)
  const [maxDate, setMaxDate] = useState(maxStartDate)

  const startDatePlusMaxDuration = addDays(startDate, 28)

  const minDate = useMemo(() => {
    const from = new Date()
    from.setHours(from.getHours() + 28)
    return from
  }, [])

  const handleDateChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      if (!dates || !Array.isArray(dates)) {
        return
      }

      const [start, end] = dates
      if (!start) {
        return
      }

      const adjustedStart = preserveDateWhenConvertedToUTC(
        adjustDateObjectTimeToUserTime(start, 28, minDate)
      )

      setStartDate(adjustedStart)
      form.setValue('startDateTime', adjustedStart)

      // dynamically set max date
      if (end === null) {
        if (startDatePlusMaxDuration > maxPossibleEndDate) {
          setMaxDate(maxPossibleEndDate) // 500 days from current date
        } else {
          setMaxDate(startDatePlusMaxDuration) // 28 days from startDate
        }
      } else {
        setMaxDate(maxStartDate) // 499 days from current date
      }

      if (end?.getTime() === adjustedStart.getTime()) {
        end.setDate(adjustedStart.getDate() + 1)
      }

      setEndDate(end)
      const formEndValue = end as unknown as Date
      form.setValue('endDateTime', formEndValue, { shouldValidate: !!end })
    },
    [form, maxPossibleEndDate, maxStartDate, startDatePlusMaxDuration, minDate]
  )

  const maxEndDate = min([startDatePlusMaxDuration, maxPossibleEndDate])

  return {
    startDate,
    endDate,
    minDate,
    maxDate,
    maxEndDate,
    handleDateChange,
  }
}

/**
 * Manages data transformations relating to the traveller room picker
 * TODO: This needs to be updated to manage the state for the field, when the field supports it
 */
export function useTravellerPickerState(form: UseFormReturn<HotelResultsFormState>) {
  const rooms = form.getValues('rooms')
  const travellerCount = useMemo(
    () =>
      rooms.reduce(
        (acc, room) => {
          acc.adultCount += room.adults
          acc.childCount += room.children
          return acc
        },
        {
          adultCount: 0,
          childCount: 0,
        }
      ),
    [rooms]
  )

  return {
    ...travellerCount,
    roomCount: rooms.length,
  }
}

type HotelTimeAndRoomErrorMap = {
  startDateTime: { [key in z.core.$ZodIssueCode]?: string }
  endDateTime: { [key in z.core.$ZodIssueCode]?: string }
  rooms: { [key in z.core.$ZodIssueCode]?: string }
}
type HotelSearchErrorMap = {
  searchString: { [key in z.core.$ZodIssueCode]?: string }
  destination: { [key in z.core.$ZodIssueCode]?: string }
} & HotelTimeAndRoomErrorMap

export type HotelSearchErrors = {
  getFieldError: (field: keyof HotelSearchErrorMap) => string | undefined
  getDestinationError: () => string | undefined
  getChildAgeError: (roomIndex: number, childIndex: number) => string | undefined
}

/**
 * Automatically converts the zod validation errors to a translated human readable error message
 */
export function useHotelSearchErrors(
  errors: UseFormReturn<HotelResultsFormState>['formState']['errors']
): HotelSearchErrors {
  const { notAValid, mustBeSelected, cannotBeEmpty, getValidationErrorMessage } =
    useFormValidationMessages()

  const t = useTranslations()
  const childAgeRequired = t('components.traveller_picker.labels.child_age_required')

  const errorMap: HotelSearchErrorMap = useMemo(
    () => ({
      searchString: { [ZodIssueCode.custom]: notAValid(t('common.forms.labels.search')) },
      destination: { [ZodIssueCode.custom]: mustBeSelected(t('common.forms.labels.destination')) },
      startDateTime: { [ZodIssueCode.custom]: notAValid(t('common.forms.labels.start_date')) },
      endDateTime: {
        [ZodIssueCode.custom]: notAValid(t('common.forms.labels.end_date')),
        [ZodIssueCode.invalid_type]: cannotBeEmpty(),
      },
      rooms: { [ZodIssueCode.too_small]: mustBeSelected(t('common.labels.room')) },
    }),
    [t, notAValid, mustBeSelected, cannotBeEmpty]
  )

  // Returns standard error regardless of specific nested errors
  const getDestinationError = useCallback(
    () => errors?.destination && errorMap.destination[ZodIssueCode.custom],
    [errors, errorMap]
  )

  const getFieldError: HotelSearchErrors['getFieldError'] = useCallback(
    (field: keyof HotelSearchErrorMap) =>
      errors?.[field] &&
      getValidationErrorMessage({
        errorMap,
        errors,
        field,
      }),
    [errors, getValidationErrorMessage, errorMap]
  )

  const getChildAgeError: HotelSearchErrors['getChildAgeError'] = useCallback(
    (roomIndex, childIndex) =>
      errors.rooms?.[roomIndex]?.childrenAges?.[childIndex]?.age ? childAgeRequired : undefined,
    [childAgeRequired, errors.rooms]
  )

  return {
    getFieldError,
    getDestinationError,
    getChildAgeError,
  }
}

export const getCurrentSearch = () => {
  if (isClient()) {
    return window.location.search
  }
  return ''
}

const sanitizeFormDefaultValues = (defaultValues: HotelResultsFormState): HotelResultsFormState => {
  const sanitizedSearchString = defaultValues.searchString
    ? sanitizeBrowserText(defaultValues.searchString, 160)
    : undefined

  return {
    ...defaultValues,
    searchString: sanitizedSearchString,
    destination: {
      ...defaultValues.destination,
      id: sanitizeBrowserIdentifier(defaultValues.destination.id, 64),
      type: sanitizeBrowserIdentifier(defaultValues.destination.type, 64),
      fullName: sanitizeBrowserText(defaultValues.destination.fullName, 160),
      name: defaultValues.destination.name
        ? sanitizeBrowserText(defaultValues.destination.name, 160)
        : defaultValues.destination.name,
    },
  }
}

/**
 * Sets up the react-hook-form and creates the submit handler and error translations
 * @param defaultValues The default values for the form state
 */
export function useHotelSearchForm(
  defaultValues: HotelResultsFormState,
  toggleRef?: ForwardedRef<{ press: () => void }>,
  destinationUpdateSourceRef?: React.RefObject<'form' | 'map' | null>
) {
  const router = useRouter()
  const { query: filtersQuery } = useFilterRouter('HOTEL')
  const { createRoutePath } = usePlatformRoutes()
  const queryClient = useQueryClient()
  const { opCo, locale } = usePlatformConfig()
  const safeDefaultValues = useMemo(() => sanitizeFormDefaultValues(defaultValues), [defaultValues])
  const form = useForm<HotelResultsFormState>({
    defaultValues: safeDefaultValues,
    resolver: zodFormResolver(HotelResultsFormStateSchema),
  })
  const { getValues } = form
  const errors = useHotelSearchErrors(form.formState.errors)

  // Reset filters if destination changed; preserve if only dates/rooms changed
  const hasDestinationChanged = useLocationChanged(parseHotelUrlDestination)

  const handleFormSubmit = useCallback(() => {
    const { startDateTime, endDateTime, destination, rooms } = getValues()

    const destinationChanged = hasDestinationChanged({ id: destination.id, type: destination.type })

    const searchData = {
      rooms,
      destination: {
        type: destination.type,
        id: destination.id,
        fullName: destination.fullName,
        name: destination.name,
      },
      startDateTime: preserveDateWhenConvertedToUTC(startDateTime),
      endDateTime: preserveDateWhenConvertedToUTC(endDateTime),
      page: 1, // this is always a new search
    }

    if (destinationUpdateSourceRef) {
      destinationUpdateSourceRef.current = 'form'
    }

    if (toggleRef && typeof toggleRef !== 'function') {
      toggleRef?.current?.press()
    }

    addSearchToLocalStorage(searchData)
    setHotelDestinationHandoff({
      destination: searchData.destination,
      signature: {
        destSearch: searchData.destination.fullName,
        destId: searchData.destination.id,
        destType: searchData.destination.type,
      },
    })
    queryClient.setQueryData(
      createAncillariesLocationsQueryKey(
        'HOTEL',
        { searchString: searchData.destination.fullName },
        opCo,
        locale
      ),
      [searchData.destination]
    )
    const query = buildHotelResultsQuery('', searchData)
    const queryWithOpCo = QueryString.stringify({
      ...QueryString.parse(query),
      // Only inherit filters if destination didn't change (dates/rooms changed only)
      ...(destinationChanged ? {} : filtersQuery ?? {}),
      page: 1,
      opco: opCo,
    })
    router.push(createRoutePath(`/hotels/results?${queryWithOpCo}`))
  }, [
    getValues,
    filtersQuery,
    hasDestinationChanged,
    destinationUpdateSourceRef,
    toggleRef,
    queryClient,
    opCo,
    locale,
    router,
    createRoutePath,
  ])

  return {
    form,
    handleFormSubmit,
    errors,
  }
}
